import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { WorksApi, CertificationsApi, uploadWorkImage, type WorkCreate, type CertType } from "../api/works";
import CreatorProfilePicker from "./CreatorProfilePicker";
import SourceFileField from "./SourceFileField";
import { buildProvenance } from "../provenance/buildProvenance";
import logger from "../analytics/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
};

const REQ_MARK = (
  <Text as="span" color="red.500" ml={1}>
    *
  </Text>
);

type FieldErrors = {
  title?: string;
  type?: string;
  format?: string;
  creator?: string;
  profile?: string;
  evidenceReason?: string;
};

export default function AddWorkModal({ isOpen, onClose, onCreated }: Props) {
  const toast = useToast();
  const titleRef = useRef<HTMLInputElement | null>(null);

  // Work fields
  const [title, setTitle] = useState("");
  const [wtype, setWtype] = useState("");
  const [wformat, setWformat] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);

  // Creator → profile (managed by CreatorProfilePicker; parent holds the ids).
  const [creatorId, setCreatorId] = useState("");
  const [profileId, setProfileId] = useState("");

  // Optional certification
  const [certTypeId, setCertTypeId] = useState("");
  const [evidence, setEvidence] = useState("");
  const [evidenceComment, setEvidenceComment] = useState("");
  const [approvedBy, setApprovedBy] = useState("");

  // Option lists
  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [formatOptions, setFormatOptions] = useState<string[]>([]);
  const [certTypes, setCertTypes] = useState<CertType[]>([]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [prevOpen, setPrevOpen] = useState(isOpen);

  // Reset everything whenever the modal closes (Save-and-add-another resets
  // partially before reopening stays open).
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (!isOpen) {
      setTitle("");
      setWtype("");
      setWformat("");
      setDescription("");
      setUrl("");
      setImageFile(null);
      setSourceFile(null);
      setCreatorId("");
      setProfileId("");
      setCertTypeId("");
      setEvidence("");
      setEvidenceComment("");
      setApprovedBy("");
      setError(null);
      setFieldErrors({});
      setBusy(false);
    }
  }

  // Load dropdown sources on open.
  useEffect(() => {
    if (!isOpen) return;
    WorksApi.types().then(setTypeOptions).catch(() => setTypeOptions([]));
    WorksApi.formats().then(setFormatOptions).catch(() => setFormatOptions([]));
    CertificationsApi.types().then(setCertTypes).catch(() => setCertTypes([]));
  }, [isOpen]);

  const validate = () => {
    const errs: FieldErrors = {};
    if (!title.trim()) errs.title = "Required";
    if (!wtype.trim()) errs.type = "Required";
    if (!wformat.trim()) errs.format = "Required";
    if (!creatorId) errs.creator = "Select a creator";
    else if (!profileId) errs.profile = "Select a profile";
    // Evidence reason is required whenever a certification is being applied.
    if (certTypeId && !evidenceComment.trim()) errs.evidenceReason = "Required";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const doSave = async (): Promise<string | null> => {
    setError(null);
    if (!validate()) return null;
    setBusy(true);
    try {
      let imgAssetKey: string | null = null;
      if (imageFile) {
        imgAssetKey = await uploadWorkImage(imageFile);
      }
      // Only meaningful alongside a certification type: without one the backend
      // creates a PENDING cert, and it writes provenance only for an APPROVED
      // one — so a block sent here would be validated and then dropped. The
      // picker is disabled in that state (SourceFileField), but the state can
      // also be reached by clearing the cert type after picking a file.
      const provenance =
        certTypeId && sourceFile ? ((await buildProvenance(sourceFile)) ?? undefined) : undefined;
      const body: WorkCreate = {
        creator_profile_id: profileId,
        title: title.trim(),
        type: wtype.trim(),
        format: wformat.trim(),
        description: description.trim() || null,
        url: url.trim() || null,
        ...(imgAssetKey ? { img_asset_key: imgAssetKey } : {}),
        ...(provenance ? { provenance } : {}),
      };
      if (certTypeId) {
        body.certification = {
          type_id: certTypeId,
          evidence: evidence.trim() || null,
          evidence_comment: evidenceComment.trim() || null,
          approved_by: approvedBy.trim() || null,
        };
      }
      const created = await WorksApi.create(body);
      toast({ status: "success", title: `Added ${created.title || created.id}` });
      onCreated();
      return created.id;
    } catch (e) {
      logger.error("add_work_failed", e);
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      const code = err?.response?.data?.error;
      setError(code || err?.message || "Failed to add work");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    const id = await doSave();
    if (id) onClose();
  };

  const handleSaveAndAddAnother = async () => {
    const id = await doSave();
    if (!id) return;
    // Preserve Type, Format, Creator (+profiles), Profile, Approved by.
    setTitle("");
    setDescription("");
    setUrl("");
    setImageFile(null);
    setSourceFile(null);
    setCertTypeId("");
    setEvidence("");
    setEvidenceComment("");
    setFieldErrors({});
    setError(null);
    setTimeout(() => titleRef.current?.focus(), 0);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (busy ? null : onClose())}
      size="3xl"
      closeOnOverlayClick={!busy}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add work</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Stack spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Title{REQ_MARK}
                </Text>
                <Input
                  ref={titleRef}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  isDisabled={busy}
                  isInvalid={!!fieldErrors.title}
                  autoFocus
                />
                {fieldErrors.title && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {fieldErrors.title}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Type{REQ_MARK}
                </Text>
                <Input
                  list="add-work-types"
                  value={wtype}
                  onChange={(e) => setWtype(e.target.value)}
                  isDisabled={busy}
                  isInvalid={!!fieldErrors.type}
                />
                <datalist id="add-work-types">
                  {typeOptions.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
                {fieldErrors.type && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {fieldErrors.type}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Format{REQ_MARK}
                </Text>
                <Input
                  list="add-work-formats"
                  value={wformat}
                  onChange={(e) => setWformat(e.target.value)}
                  isDisabled={busy}
                  isInvalid={!!fieldErrors.format}
                />
                <datalist id="add-work-formats">
                  {formatOptions.map((f) => (
                    <option key={f} value={f} />
                  ))}
                </datalist>
                {fieldErrors.format && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {fieldErrors.format}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Description
                </Text>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  isDisabled={busy}
                  rows={3}
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  URL
                </Text>
                <Input value={url} onChange={(e) => setUrl(e.target.value)} isDisabled={busy} />
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Work image
                </Text>
                <Input
                  type="file"
                  accept="image/*"
                  p={1}
                  isDisabled={busy}
                  onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                />
                {imageFile && (
                  <HStack mt={2} spacing={3} align="center">
                    <Image
                      src={URL.createObjectURL(imageFile)}
                      alt="Selected work image"
                      boxSize="64px"
                      objectFit="cover"
                      borderRadius={6}
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      isDisabled={busy}
                      onClick={() => setImageFile(null)}
                    >
                      Remove
                    </Button>
                  </HStack>
                )}
              </Box>

              <SourceFileField
                value={sourceFile}
                onChange={setSourceFile}
                workType={wtype}
                isDisabled={busy}
                certificationSelected={!!certTypeId}
              />
            </Stack>

            <Stack spacing={4}>
              <CreatorProfilePicker
                value={{ creatorId, profileId }}
                onChange={(v) => {
                  setCreatorId(v.creatorId);
                  setProfileId(v.profileId);
                }}
                disabled={busy}
                requiredMark
                creatorError={fieldErrors.creator}
                profileError={fieldErrors.profile}
              />

              <Box>
                <Text fontSize="sm" mb={1}>
                  Certification type
                </Text>
                <Select
                  placeholder="(none)"
                  value={certTypeId}
                  onChange={(e) => setCertTypeId(e.target.value)}
                  isDisabled={busy}
                >
                  {certTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} v{t.version}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Evidence data
                </Text>
                <Textarea
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value)}
                  isDisabled={busy || !certTypeId}
                  rows={2}
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Evidence reason{certTypeId ? " *" : ""}
                </Text>
                <Textarea
                  value={evidenceComment}
                  onChange={(e) => setEvidenceComment(e.target.value)}
                  isDisabled={busy || !certTypeId}
                  isInvalid={!!fieldErrors.evidenceReason}
                  rows={2}
                />
                {fieldErrors.evidenceReason && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    {fieldErrors.evidenceReason}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Approved by
                </Text>
                <Input
                  value={approvedBy}
                  onChange={(e) => setApprovedBy(e.target.value)}
                  isDisabled={busy || !certTypeId}
                />
              </Box>
            </Stack>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button colorScheme="blue" mr={3} onClick={handleSave} isLoading={busy}>
            Save
          </Button>
          <Button colorScheme="blue" onClick={handleSaveAndAddAnother} isLoading={busy}>
            Save and add another
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
